import React, { useState, useEffect } from 'react';
import CargasTimeline from './CargasTimeline';

const isValidValue = (value) => {
    if (value === null || value === undefined) return false;
    const num = parseFloat(value);
    return !isNaN(num) && num > 0;
};

const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return 'No consta';
    return num.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
};

// Safe total computation that avoids double-counting items in the same group by taking the max value per group
const computeDeduplicatedTotal = (cargasArray) => {
    const groupedValues = {};
    cargasArray.forEach(item => {
        const c = item.carga ? item.carga : item; // handle both wrapped UI entries and raw AI JSON logs
        const val = parseFloat(c.valor) || 0;
        
        // Use group ID to deduplicate. If none, fall back to unique ID.
        const deduplicationKey = c.grupo || c.id || Math.random().toString();
        
        groupedValues[deduplicationKey] = Math.max(groupedValues[deduplicationKey] || 0, val);
    });
    return Object.values(groupedValues).reduce((acc, v) => acc + v, 0);
};

const CargaCard = ({ carga, status, statusLabel, textColor, bgColor, borderColor, iconClass, razon, afeccionExtra }) => (
    <div className={`p-4 rounded-lg border ${borderColor} ${bgColor} mb-3 transition-all duration-200 hover:shadow-md`}>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <i className={`${iconClass} ${textColor}`}></i>
                    <span className={`text-xs font-bold uppercase tracking-wider ${textColor} px-2 py-0.5 rounded-full ${bgColor} border ${borderColor}`}>
                        {statusLabel}
                    </span>
                </div>
                <h4 className="text-base font-semibold text-gray-900 mt-2">{carga.nombre}</h4>
                <p className="text-sm text-gray-500 mt-0.5">
                    {carga.fecha ? carga.fecha : carga.num_inscripcion}
                </p>
                {carga.descripcion && (
                    <p className="text-sm text-gray-600 mt-1">{carga.descripcion}</p>
                )}
                {razon && (
                    <p className={`text-sm mt-2 ${textColor} font-medium leading-relaxed`}>
                        <i className="fas fa-info-circle mr-1"></i>
                        {razon}
                        {afeccionExtra || ''}
                    </p>
                )}
            </div>
            <div className="text-right flex-shrink-0">
                <p className="text-lg font-bold text-gray-900">
                    {carga.valor && carga.valor !== 'No consta' && parseFloat(carga.valor) > 0
                        ? formatCurrency(carga.valor)
                        : <span className="text-sm text-gray-400 font-normal">Sin valor</span>
                    }
                </p>
            </div>
        </div>
    </div>
);

const SectionHeader = ({ icon, iconColor, title, count, totalValue, bgGradient }) => (
    <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between ${bgGradient} rounded-lg px-4 py-3 mb-3`}>
        <div className="flex items-center gap-2">
            <i className={`${icon} ${iconColor} text-lg`}></i>
            <h3 className="text-lg font-bold text-gray-800">{title}</h3>
            <span className="bg-white text-gray-600 text-xs font-bold px-2 py-0.5 rounded-full shadow-sm">
                {count}
            </span>
        </div>
        {totalValue !== null && totalValue !== undefined && (
            <p className="text-sm font-semibold text-gray-700 mt-1 sm:mt-0">
                Total: {formatCurrency(totalValue)}
            </p>
        )}
    </div>
);

const AnalysisResult = ({ data, subastaId }) => {
    const [activeTab, setActiveTab] = useState(1);
    const [collapsedSections, setCollapsedSections] = useState({});
    const [viewMode, setViewMode] = useState({}); // Stores view mode per lote: 'tarjetas' | 'cronologia'

    useEffect(() => {
        console.log(data);
    }, [data]);



    const toggleSection = (key) => {
        setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const totalValorTasacion = data.bienes_subastados.reduce((acc, bien) => {
        const v = parseFloat(bien.valor);
        return acc + (isNaN(v) ? 0 : v);
    }, 0);
    const hasAnyTasacion = data.bienes_subastados.some(b => isValidValue(b.valor));

    // Classify cargas for a given bien
    const classifyCargas = (item) => {
        const noCanceladas = [];
        const canceladas = [];
        const requierenRevision = [];

        item.cargas_bien.forEach((c) => {
            const cargaNoCancelada = item.cargas_no_canceladas.find(carga => carga.id === c.id);
            const cargaCancelada = item.cargas_canceladas.find(carga => carga.id === c.id);

            const afeccionExtra = c.nombre?.toLowerCase() === 'afección fiscal'
                ? ' Suele hacer referencia a deudas con la Hacienda Pública relacionadas directamente con la propiedad, mayoritariamente impagos del IBI.'
                : '';

            const isEjecutada = 
                (item.cargas_ejecutadas && item.cargas_ejecutadas.some(e => e.id === c.id)) || 
                (item.carga_ejecutada && item.carga_ejecutada.id === c.id);

            // Fetch the specific executing charge (to get its custom reason if any)
            const cargaEjecutadaData = 
                (item.cargas_ejecutadas && item.cargas_ejecutadas.find(e => e.id === c.id)) || 
                item.carga_ejecutada;

            if (isEjecutada) {
                // Carga ejecutada goes into cancelled
                canceladas.push({
                    carga: c,
                    status: 'ejecutada',
                    statusLabel: 'EJECUTADA – Se cancela tras la subasta',
                    textColor: 'text-sky-600',
                    bgColor: 'bg-sky-50',
                    borderColor: 'border-sky-200',
                    iconClass: 'fas fa-gavel',
                    razon: cargaEjecutadaData?.razon || cargaCancelada?.razon || 'Es la carga que motiva la subasta y se cancela tras la misma.',
                    afeccionExtra,
                });
            } else if (c.estado?.toLowerCase() === 'caducada') {
                // Intercept caducadas even if mistakenly placed in no_canceladas array
                canceladas.push({
                    carga: c,
                    status: 'caducada',
                    statusLabel: 'CADUCADA',
                    textColor: 'text-green-600',
                    bgColor: 'bg-green-50',
                    borderColor: 'border-green-200',
                    iconClass: 'fas fa-clock',
                    razon: cargaNoCancelada?.razon || cargaCancelada?.razon || '',
                    afeccionExtra,
                });
            } else if (cargaNoCancelada) {
                noCanceladas.push({
                    carga: c,
                    status: 'no_cancelada',
                    statusLabel: 'NO SE CANCELA tras la subasta',
                    textColor: 'text-red-600',
                    bgColor: 'bg-red-50',
                    borderColor: 'border-red-200',
                    iconClass: 'fas fa-exclamation-circle',
                    razon: cargaNoCancelada.razon,
                    afeccionExtra,
                });
            } else if (cargaCancelada) {
                canceladas.push({
                    carga: c,
                    status: 'cancelable',
                    statusLabel: 'SE CANCELA tras la subasta',
                    textColor: 'text-green-600',
                    bgColor: 'bg-green-50',
                    borderColor: 'border-green-200',
                    iconClass: 'fas fa-check-circle',
                    razon: cargaCancelada.razon,
                    afeccionExtra,
                });
            } else {
                // Check if posterior to ejecutada
                let razon = 'La información de la carga no está disponible o es demasiado compleja para ser analizada automáticamente. Se recomienda revisión por un profesional.';
                let statusLabel = 'REQUIERE REVISIÓN';

                const currentIndex = item.cargas_bien.findIndex(carga => carga.id === c.id);
                // Get all ejecutadas indices
                const ejecutadasIndices = [];
                if (item.cargas_ejecutadas) {
                    item.cargas_ejecutadas.forEach(e => {
                        const idx = item.cargas_bien.findIndex(carga => carga.id === e.id);
                        if (idx !== -1) ejecutadasIndices.push(idx);
                    });
                } else if (item.carga_ejecutada) {
                    const idx = item.cargas_bien.findIndex(carga => carga.id === item.carga_ejecutada.id);
                    if (idx !== -1) ejecutadasIndices.push(idx);
                }

                if (ejecutadasIndices.length > 0) {
                    // Check if current charge is after any executing charge
                    const isPosterior = ejecutadasIndices.some(idx => currentIndex > idx);
                    if (isPosterior) {
                        razon = 'Esta carga es posterior a la carga o hipoteca ejecutada, por lo que en principio debería cancelarse con la subasta. Sin embargo, el análisis no ha podido confirmarlo de forma expresa. Se recomienda verificar con la documentación del procedimiento.';
                        statusLabel = 'POSIBLEMENTE CANCELABLE – Requiere revisión';
                    }
                }

                requierenRevision.push({
                    carga: c,
                    status: 'revision',
                    statusLabel,
                    textColor: 'text-amber-600',
                    bgColor: 'bg-amber-50',
                    borderColor: 'border-amber-200',
                    iconClass: 'fas fa-question-circle',
                    razon,
                    afeccionExtra,
                });
            }
        });

        return { noCanceladas, canceladas, requierenRevision };
    };

    const totalCargasNoCanceladas = data.bienes_subastados.reduce((acc, bien) => {
        const { noCanceladas } = classifyCargas(bien);
        return acc + computeDeduplicatedTotal(noCanceladas);
    }, 0);

    return (
        <div className='h-full w-full flex flex-col items-start'>
            {/* === Report Header === */}
            <div className="w-full max-w-4xl mx-auto mt-4 mb-8">
                {/* Subasta code + inline metrics */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6">
                    <div>
                        <a
                            href={`https://subastas.boe.es/reg/detalleSubasta.php?idSub=${subastaId || data.subasta.codigo}`}
                            target='_blank'
                            rel='noreferrer'
                            className="text-2xl font-bold text-gray-900 hover:text-blue-700 transition-colors"
                        >
                            {subastaId || data.subasta.codigo}
                            <i className="fas fa-external-link-alt text-xs ml-2 text-gray-400"></i>
                        </a>
                        {data.subasta.tipo && (
                            <p className="text-sm text-gray-500 mt-0.5">{data.subasta.tipo}</p>
                        )}
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                        <span className="text-gray-600">
                            <i className="fas fa-house text-gray-400 mr-1"></i>
                            <strong>{data.subasta.num_lotes}</strong> {data.subasta.num_lotes === 1 || data.subasta.num_lotes === '1' ? 'lote' : 'lotes'}
                        </span>
                        <span className="text-gray-300">|</span>
                        <span className="text-gray-600">
                            <i className="fas fa-coins text-gray-400 mr-1"></i>
                            Tasación: <strong>{hasAnyTasacion ? formatCurrency(totalValorTasacion) : 'N/D'}</strong>
                        </span>
                        <span className="text-gray-300">|</span>
                        <span className="text-red-600">
                            <i className="fas fa-exclamation-circle text-red-400 mr-1"></i>
                            Cargas: <strong>{formatCurrency(totalCargasNoCanceladas)}</strong>
                        </span>
                    </div>
                </div>

                {/* Executive summary */}
                {data.resumen && (
                    <div className="border-l-4 border-blue-500 pl-5 py-1 mb-5">
                        <p className="text-gray-700 leading-relaxed">{data.resumen}</p>
                    </div>
                )}

                {/* Additional observations */}
                {data.otros && (
                    <div className="border-l-4 border-amber-400 pl-5 py-1 mb-2">
                        <p className="text-sm text-gray-600 leading-relaxed">
                            <strong className="text-amber-700">Información adicional:</strong> {data.otros}
                        </p>
                    </div>
                )}
            </div>

            {/* Lote Tabs (only if multiple lotes) */}
            {data.bienes_subastados.length > 1 && (
                <div className="w-full max-w-4xl mx-auto mb-6">
                    <div className="flex flex-wrap items-center justify-center gap-2">
                        {data.bienes_subastados.map((item, index) => (
                            <button
                                key={index + 1}
                                onClick={() => setActiveTab(index + 1)}
                                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
                                    activeTab === index + 1
                                        ? 'bg-blue-600 text-white shadow-md'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                                }`}
                            >
                                {`Lote ${index + 1}${item.nombre ? ` – ${item.nombre}` : ''}`}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Content */}
            <div className="w-full max-w-4xl mx-auto px-2">
                {data.bienes_subastados.map((item, index) => {
                    if (activeTab !== index + 1) return null;

                    const { noCanceladas, canceladas, requierenRevision } = classifyCargas(item);

                    const totalNoCanceladasValue = computeDeduplicatedTotal(noCanceladas);
                    const totalRequierenRevisionValue = computeDeduplicatedTotal(requierenRevision);

                    return (
                        <div key={index} className="p-6 bg-blueGray-50 border border-gray-200 rounded-lg shadow dark:bg-gray-800 dark:border-gray-700 mb-4">
                            {/* Bien header */}
                            <div className="flex flex-row flex-wrap items-center">
                                <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                                    {item.nombre || 'Bien subastado'}
                                </h3>
                                {item.catastro_link && (
                                    <a
                                        href={item.catastro_link}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center px-3 py-2 ml-4 text-sm font-medium text-center text-white bg-blue-700 rounded-lg hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                                    >
                                        Catastro
                                        <i className="fas fa-external-link-alt ml-2"></i>
                                    </a>
                                )}
                            </div>
                            <p className="my-3 font-normal text-gray-700 dark:text-gray-400">{item.descripcion}</p>

                            {/* Summary Cards */}
                            <div className={`grid grid-cols-1 ${item.valor_estimado_mercado ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} gap-4 mt-4 mb-8`}>
                                {/* Valor de Tasación Card */}
                                <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-5 flex flex-col items-center justify-center text-center shadow-sm">
                                    <i className="fas fa-sack-dollar text-blue-500 text-2xl mb-2"></i>
                                    <p className="text-sm font-semibold text-blue-700 uppercase tracking-wider mb-1">Valor de Tasación</p>
                                    {isValidValue(item.valor) ? (
                                        <p className="text-2xl font-bold text-blue-900">
                                            {formatCurrency(item.valor)}
                                        </p>
                                    ) : (
                                        <p className="text-lg font-medium text-gray-400">No disponible</p>
                                    )}
                                </div>
                                {/* Valor Estimado de Mercado Card */}
                                {item.valor_estimado_mercado && (
                                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-5 flex flex-col items-center justify-center text-center shadow-sm relative">
                                        <span className="absolute top-2 right-2 bg-purple-200 text-purple-700 text-xs font-bold px-2 py-0.5 rounded-full">
                                            Estimación IA
                                        </span>
                                        <i className="fas fa-chart-line text-purple-500 text-2xl mb-2"></i>
                                        <p className="text-sm font-semibold text-purple-700 uppercase tracking-wider mb-1">Valor de Mercado</p>
                                        <p className="text-2xl font-bold text-purple-900">
                                            {formatCurrency(item.valor_estimado_mercado)}
                                        </p>
                                    </div>
                                )}
                                {/* Cargas No Canceladas Total */}
                                <div className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-xl p-5 flex flex-col items-center justify-center text-center shadow-sm">
                                    <i className="fas fa-triangle-exclamation text-red-500 text-2xl mb-2"></i>
                                    <p className="text-sm font-semibold text-red-700 uppercase tracking-wider mb-1">Cargas No Canceladas</p>
                                    <p className="text-2xl font-bold text-red-900">
                                        {formatCurrency(totalNoCanceladasValue)}
                                    </p>
                                </div>
                            </div>

                            {/* View Toggle */}
                            <div className="flex justify-center mb-6">
                                <div className="inline-flex bg-gray-200 rounded-lg p-1">
                                    <button
                                        onClick={() => setViewMode(prev => ({ ...prev, [index]: 'tarjetas' }))}
                                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                            (!viewMode[index] || viewMode[index] === 'tarjetas')
                                                ? 'bg-white text-gray-900 shadow-sm'
                                                : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                    >
                                        <i className="fas fa-layer-group mr-2"></i>
                                        Vista Clasificada
                                    </button>
                                    <button
                                        onClick={() => setViewMode(prev => ({ ...prev, [index]: 'cronologia' }))}
                                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                            viewMode[index] === 'cronologia'
                                                ? 'bg-white text-gray-900 shadow-sm'
                                                : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                    >
                                        <i className="fas fa-stream mr-2"></i>
                                        Cronología y Grupos
                                    </button>
                                </div>
                            </div>

                            {/* Main Content Area */}
                            {viewMode[index] === 'cronologia' ? (
                                <CargasTimeline 
                                    noCanceladas={noCanceladas} 
                                    canceladas={canceladas} 
                                    requierenRevision={requierenRevision}
                                    gruposCargas={item.grupos_cargas}
                                />
                            ) : (
                                <>
                                    {/* ===== CARGAS NO CANCELADAS ===== */}
                                    {noCanceladas.length > 0 && (
                                        <div className="mb-6">
                                            <button
                                                onClick={() => toggleSection(`nc-${index}`)}
                                                className="w-full text-left"
                                            >
                                                <SectionHeader
                                                    icon="fas fa-exclamation-circle"
                                                    iconColor="text-red-500"
                                                    title="Cargas que NO se cancelan"
                                                    count={noCanceladas.length}
                                                    totalValue={totalNoCanceladasValue}
                                                    bgGradient="bg-gradient-to-r from-red-50 to-red-100"
                                                />
                                            </button>
                                            {!collapsedSections[`nc-${index}`] && (
                                                <div className="mt-1">
                                                    {noCanceladas.map((entry, i) => (
                                                        <CargaCard key={i} {...entry} />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* ===== CARGAS QUE REQUIEREN REVISIÓN ===== */}
                                    {requierenRevision.length > 0 && (
                                        <div className="mb-6">
                                            <button
                                                onClick={() => toggleSection(`rv-${index}`)}
                                                className="w-full text-left"
                                            >
                                                <SectionHeader
                                                    icon="fas fa-question-circle"
                                                    iconColor="text-amber-500"
                                                    title="Requieren revisión"
                                                    count={requierenRevision.length}
                                                    totalValue={totalRequierenRevisionValue}
                                                    bgGradient="bg-gradient-to-r from-amber-50 to-amber-100"
                                                />
                                            </button>
                                            {!collapsedSections[`rv-${index}`] && (
                                                <div className="mt-1">
                                                    {requierenRevision.map((entry, i) => (
                                                        <CargaCard key={i} {...entry} />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* ===== CARGAS CANCELADAS ===== */}
                                    {canceladas.length > 0 && (
                                        <div className="mb-6">
                                            <button
                                                onClick={() => toggleSection(`ca-${index}`)}
                                                className="w-full text-left"
                                            >
                                                <SectionHeader
                                                    icon="fas fa-check-circle"
                                                    iconColor="text-green-500"
                                                    title="Cargas que se cancelan o están caducadas"
                                                    count={canceladas.length}
                                                    totalValue={null}
                                                    bgGradient="bg-gradient-to-r from-green-50 to-green-100"
                                                />
                                            </button>
                                            {!collapsedSections[`ca-${index}`] && (
                                                <div className="mt-1">
                                                    {canceladas.map((entry, i) => (
                                                        <CargaCard key={i} {...entry} />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Disclaimer */}
            <div className="w-full p-4 mb-16 mt-4 text-sm text-blue-800 rounded-xl bg-blue-50 dark:bg-gray-800 dark:text-blue-400" role="alert">
                <div className="flex items-center px-4 pt-4 pb-3">
                    <i className="fas fa-exclamation-triangle mr-2 text-lg"></i>
                    <span className="font-medium text-base">Subastas BOE Pro puede cometer errores. Consulta siempre con un experto antes de invertir tu dinero.</span>
                </div>
                <div className="pb-4 px-4">
                    <p>La información proporcionada por Subastas BOE Pro es generada mediante inteligencia artificial y se ofrece exclusivamente con fines informativos. En ningún caso debe interpretarse como asesoramiento legal, financiero, fiscal, profesional ni de ningún otro tipo. Si decide utilizar esta información para tomar decisiones de inversión, compra, venta, licitación o cualquier otra acción, lo hace bajo su propia responsabilidad y riesgo. Subastas BOE Pro y sus propietarios no garantizan la exactitud, integridad, actualidad o validez de los datos analizados, y no asumen responsabilidad alguna por pérdidas, daños o consecuencias derivadas del uso de este servicio, incluidos aquellos que resulten de decisiones basadas en información inexacta, incompleta o errónea. Antes de emprender cualquier acción que pueda afectar su situación legal, financiera o patrimonial, consulte con profesionales calificados que puedan ofrecerle asesoramiento personalizado.</p>
                </div>
            </div>
        </div>
    );
};

export default AnalysisResult;